import type {
  SentryBoundaryLevel,
  SentryCrashOrigin,
  SentryCrashType,
  SentryModule,
} from '../types';

export const BOUNDARY_LEVEL = {
  APP: 'app',
  CONTAINER: 'container',
} as const satisfies Record<string, SentryBoundaryLevel>;

export const DEFAULT_BOUNDARY_LEVEL: SentryBoundaryLevel = BOUNDARY_LEVEL.CONTAINER;

export const CRASH_ORIGIN = {
  LOCAL: 'local',
  FEDERATED: 'federated',
  API: 'api',
  CHUNK: 'chunk',
  UNKNOWN: 'unknown',
} as const satisfies Record<string, SentryCrashOrigin>;

export const BOUNDARY_CRASH_TYPE = {
  APP_SHELL: 'app_shell_crash',
  CONTAINER_SECTION: 'container_section_crash',
} as const satisfies Record<string, SentryCrashType>;

export const BOUNDARY_LABEL = {
  APP: 'app_error_boundary',
  container: (scope: string): string => `container_crash:${scope}`,
} as const;

export const BOUNDARY_ERROR_CONFIG = {
  product: 'PG',
  team: 'FP',
  repo_url: 'https://github.com/cashfree-tech/merchantwebapp.git',
} as const;

export const ERROR_BOUNDARY_METADATA = {
  modal: {
    paymentsRefund: {
      module: 'payments' as SentryModule,
      feature: 'refund_modal',
    },
    paymentLinksCreate: {
      module: 'payment_links' as SentryModule,
      feature: 'create_link',
    },
    paymentLinksBatchUpload: {
      module: 'payment_links' as SentryModule,
      feature: 'batch_upload',
    },
  },
  summary: {
    charts: {
      module: 'summary' as SentryModule,
      feature: 'summary_charts',
    },
    recentSettlements: {
      module: 'settlements' as SentryModule,
      feature: 'summary_recent_settlements',
      crashOrigin: CRASH_ORIGIN.LOCAL,
    },
    openDisputes: {
      module: 'disputes' as SentryModule,
      feature: 'summary_open_disputes',
    },
    riskShield: {
      module: 'risk_shield' as SentryModule,
      feature: 'summary_riskshield',
      crashOrigin: CRASH_ORIGIN.LOCAL,
    },
    pendingPaymentVerification: {
      module: 'payments' as SentryModule,
      feature: 'summary_pending_payment_verification',
    },
    suspicious: {
      module: 'suspicious' as SentryModule,
      feature: 'summary_suspicious',
    },
  },
  analytics: {
    businessTrends: {
      module: 'payments' as SentryModule,
      feature: 'summary_analytics_business_trends',
    },
    conversionRate: {
      module: 'payments' as SentryModule,
      feature: 'summary_analytics_conversion_rate',
    },
    paymentFailure: {
      module: 'payments' as SentryModule,
      feature: 'summary_analytics_payment_failure',
    },
    srOverview: {
      module: 'payments' as SentryModule,
      feature: 'summary_analytics_sr_overview',
    },
  },
  settingsV2: {
    products: {
      module: 'pg_app' as SentryModule,
      feature: 'settings_v2_products',
      componentName: 'Settings Products',
    },
    general: {
      module: 'pg_app' as SentryModule,
      feature: 'settings_v2_general',
      componentName: 'Settings General',
    },
  },
  // Home (landingV2) / Insights: product owners when clear; otherwise home/insights.
  // Do not use cpss here — CPSS owns backend APIs, not dashboard frontend.
  insights: {
    businessMetrics: {
      module: 'insights' as SentryModule,
      feature: 'insights_business_metrics',
      componentName: 'Business Performance',
    },
    paymentMetrics: {
      module: 'payments' as SentryModule,
      feature: 'insights_payment_metrics',
      componentName: 'Payment Performance',
    },
    conversionMetrics: {
      module: 'one_click_checkout' as SentryModule,
      feature: 'insights_conversion_metrics',
      componentName: 'Conversion and Checkout',
    },
    // Business Performance cards
    grossSales: {
      module: 'payments' as SentryModule,
      feature: 'gross_sales',
      componentName: 'Gross Sales',
    },
    netSales: {
      module: 'pg_app' as SentryModule,
      feature: 'net_sales',
      componentName: 'Net Sales',
    },
    refunds: {
      module: 'refunds' as SentryModule,
      feature: 'refunds',
      componentName: 'Refunds',
    },
    orders: {
      module: 'orders' as SentryModule,
      feature: 'orders',
      componentName: 'Orders',
    },
    regionalPerformance: {
      module: 'payments' as SentryModule,
      feature: 'regional_performance',
      componentName: 'Regional Performance',
    },
    paymentModes: {
      module: 'payments' as SentryModule,
      feature: 'preferred_payment_modes',
      componentName: 'Preferred Payment Modes',
    },
    customerDistribution: {
      module: 'customers' as SentryModule,
      feature: 'customers',
      componentName: 'Customers',
    },
    topCustomers: {
      module: 'customers' as SentryModule,
      feature: 'top_customers',
      componentName: 'Top Customers',
    },
    // Payment Performance cards
    overallSuccessRate: {
      module: 'payments' as SentryModule,
      feature: 'overall_success_rate',
      componentName: 'Overall Success Rate',
    },
    paymentModeSuccessRate: {
      module: 'payments' as SentryModule,
      feature: 'sr_by_payment_mode',
      componentName: 'SR by Payment Mode',
    },
    paymentMethodSplit: {
      module: 'payments' as SentryModule,
      feature: 'payment_method_split',
      componentName: 'Payment Method Split',
    },
    failureAnalysis: {
      module: 'payments' as SentryModule,
      feature: 'failure_analysis',
      componentName: 'Failure Analysis',
    },
    // Conversion and Checkout cards
    checkoutFunnel: {
      module: 'one_click_checkout' as SentryModule,
      feature: 'checkout_funnel',
      componentName: 'Checkout Funnel',
    },
    conversionRate: {
      module: 'one_click_checkout' as SentryModule,
      feature: 'checkout_conversion_trend',
      componentName: 'Checkout Conversion Trend',
    },
    couponDrivenOrders: {
      module: 'offers' as SentryModule,
      feature: 'coupon_driven_orders',
      componentName: 'Coupon-Driven Orders',
    },
    newOffers: {
      module: 'offers' as SentryModule,
      feature: 'new_offers',
      componentName: 'New Offers',
    },
    topOffers: {
      module: 'offers' as SentryModule,
      feature: 'top_offers',
      componentName: 'Top Offers',
    },
    failedOffers: {
      module: 'offers' as SentryModule,
      feature: 'failed_offers',
      componentName: 'Failed Offers',
    },
    offerSummary: {
      module: 'offers' as SentryModule,
      feature: 'offer_summary',
      componentName: 'Offer Summary',
    },
  },
  ordersDescription: {
    orderTags: {
      module: 'orders' as SentryModule,
      feature: 'orders_description_order_tags',
      componentName: 'Order Tags',
    },
  },
  home: {
    totalEarned: {
      module: 'payments' as SentryModule,
      feature: 'home_total_earned',
      componentName: 'Total Earned',
    },
    lastSettled: {
      module: 'settlements' as SentryModule,
      feature: 'home_last_settled',
      componentName: 'Last Settled',
    },
    openDisputes: {
      module: 'disputes' as SentryModule,
      feature: 'home_open_disputes',
      componentName: 'Open Disputes',
    },
    recentTransactions: {
      module: 'payments' as SentryModule,
      feature: 'home_recent_transactions',
      componentName: 'Recent Transactions',
    },
    quickActions: {
      module: 'home' as SentryModule,
      feature: 'home_quick_actions',
      componentName: 'Quick Actions',
    },
    support: {
      module: 'home' as SentryModule,
      feature: 'home_support',
      componentName: 'Support',
    },
  },
  login: {
    page: {
      module: 'login' as SentryModule,
      feature: 'login',
      componentName: 'Login',
    },
  },
  signup: {
    page: {
      module: 'signup' as SentryModule,
      feature: 'signup',
      componentName: 'Signup',
    },
  },
  // Old product-picker landing (/merchantwebapp/landing)
  landing: {
    page: {
      module: 'landing' as SentryModule,
      feature: 'landing_page',
      componentName: 'Landing Page',
    },
    productList: {
      module: 'landing' as SentryModule,
      feature: 'landing_product_list',
      componentName: 'Product List',
    },
  },
  // Shell chrome in v2 MainLayout (pathname fallback supplies page module when unset)
  mainLayout: {
    notifications: {
      feature: 'main_layout_notifications',
      componentName: 'Notifications',
    },
    helpSupport: {
      feature: 'main_layout_help_support',
      componentName: 'Help Support',
    },
    quickCreate: {
      module: 'home' as SentryModule,
      feature: 'home_quick_create',
      componentName: 'Quick Create',
    },
  },
  federated: {
    manualApproval: {
      module: 'payments' as SentryModule,
      feature: 'manual_approval',
      remoteName: 'MANUAL_APPROVAL',
    },
    addToRiskshield: {
      module: 'risk_shield' as SentryModule,
      feature: 'add_to_blacklist',
      remoteName: 'ADD_TO_RISKSHIELD',
    },
    riskShieldRemote: {
      module: 'risk_shield' as SentryModule,
      feature: 'riskshield_remote',
      remoteName: 'RISKSHIELD',
    },
    riskMetricsRemote: {
      module: 'risk_shield' as SentryModule,
      feature: 'insights_risk_metrics',
      componentName: 'Risk Metrics',
      remoteName: 'RISK_METRICS',
    },
    helpCenterForm: {
      module: 'payment_forms' as SentryModule,
      feature: 'help_center_form',
      remoteName: 'PAYMENT_FORMS',
    },
    notificationRemote: {
      feature: 'common_notifications',
      remoteName: 'COMMON_NOTIFICATIONS',
    },
  },
} as const;

/** Product card → Sentry module (keyed by productName and/or product id). */
export const LANDING_PRODUCT_MODULE_BY_KEY: Readonly<
  Record<string, SentryModule>
> = {
  // PRODUCT.* names
  PG: 'payments',
  'Payment Links': 'payment_links',
  'Payment Forms': 'payment_forms',
  'Payment Button': 'payment_buttons',
  Subscription: 'subscriptions',
  Payouts: 'payouts',
  Cashgram: 'payouts',
  'Global Collections': 'global_collection',
  'Banking as a Service': 'vba',
  'Cross Border': 'cross_border',
  'Lending as a Service': 'landing',
  'Verification Suites': 'vswebapp',
  Biller: 'bbps',
  'BBPS COU': 'bbps',
  softPOS: 'softpos',
  Router: 'flowwise',
  'International Payment Gateway': 'international_payment_gateway',
  'PG Risk Shield': 'risk_shield',
  'PO Risk Shield': 'risk_shield',
  Ecom360: 'one_click_checkout',
  'Easy Split': 'easy_split',
  Relay: 'relay',
  // productMapping ids / codes
  ORDERS: 'payment_links',
  PF: 'payment_forms',
  PB: 'payment_buttons',
  CSP: 'payouts',
  CG: 'payouts',
  SBC: 'subscriptions',
  VRS: 'vswebapp',
  BAAS: 'vba',
  LAAS: 'landing',
  LRS: 'cross_border',
  GCL: 'global_collection',
  BBPS: 'bbps',
  BBPS_COU: 'bbps',
  EPOS: 'softpos',
  CES: 'easy_split',
  IPG: 'international_payment_gateway',
  ROUTER: 'flowwise',
  PGRS: 'risk_shield',
  PORS: 'risk_shield',
  WORKFLOW_BUILDER: 'relay',
  ECOM360: 'one_click_checkout',
};

export const landingProductBoundaryMeta = (
  productName?: string,
  productId?: string,
  title?: string,
): {
  module: SentryModule;
  feature: string;
  componentName: string;
} => {
  const module =
    (productName && LANDING_PRODUCT_MODULE_BY_KEY[productName]) ||
    (productId && LANDING_PRODUCT_MODULE_BY_KEY[productId]) ||
    ('landing' as SentryModule);
  const key = (productId || productName || 'unknown')
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '_');
  return {
    module,
    feature: `landing_product_${key}`,
    componentName: title || productName || productId || 'Landing Product',
  };
};
